/**
 * Window maths for the span ribbon: which slice the lanes show, and where a
 * scrubber drag lands. Kept DOM-free so it can be tested without rendering —
 * every off-by-one in the widget lives here.
 */

/**
 * `jump` re-centres the window on the pointer — pressing the track outside the
 * window. `grab` carries it along instead, keeping the span you took hold of
 * under the pointer. `left`/`right` resize from one edge.
 */
export type DragMode = "jump" | "grab" | "left" | "right"

// The drag as it stood when the pointer went down.
export interface WindowDragOrigin {
  mode: DragMode
  // 0-based span index the window started at.
  startAtDown: number
  // Window width in spans.
  sizeAtDown: number
  // Span index under the pointer, which is what `grab` holds on to.
  atDown: number
}

export interface SpanWindow {
  start: number
  size: number
}

export const clampWindowValue = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

// Tick spacing that keeps the axis labels from colliding at the current zoom.
export function tickStepFor(windowSize: number): number {
  if (windowSize <= 40) return 10
  if (windowSize <= 120) return 25
  return 50
}

/**
 * 1-based span indexes to label: the window's first span, then every multiple of
 * `step` inside it. Absolute, not window-relative, so a label stays with its
 * span as the window slides.
 */
export function axisTickIndexes(start: number, size: number, step: number): number[] {
  const first = start + 1
  const last = start + size
  const ticks = [first]
  for (let value = Math.ceil(first / step) * step; value <= last; value += step) {
    // Skip a multiple that would crowd the leading label.
    if (value - first >= step / 2) ticks.push(value)
  }
  return ticks
}

// Pointer x as 0..1 across a track. A zero-width rect (jsdom) reads as 0.
export function fractionFromClientX(clientX: number, rect: DOMRect): number {
  if (rect.width <= 0) return 0
  return clampWindowValue((clientX - rect.left) / rect.width, 0, 1)
}

/**
 * Where the window lands for a pointer at `frac` along the overview. Every drag
 * mode's arithmetic — and every clamp — lives here, so no caller can enforce a
 * different floor or let an edge cross its opposite.
 */
export function nextWindow(
  origin: WindowDragOrigin,
  frac: number,
  total: number,
  minWindow: number,
): SpanWindow {
  const at = Math.round(frac * total)
  const floor = Math.min(minWindow, total)

  if (origin.mode === "left") {
    // The right edge is pinned; the window grows and shrinks from the left.
    const right = origin.startAtDown + origin.sizeAtDown
    const start = clampWindowValue(at, 0, right - floor)
    return { start, size: right - start }
  }

  if (origin.mode === "right") {
    const end = clampWindowValue(at, origin.startAtDown + floor, total)
    return { start: origin.startAtDown, size: end - origin.startAtDown }
  }

  const size = origin.sizeAtDown
  const maxStart = Math.max(0, total - size)

  // Grab: shift by how far the pointer has travelled, so the window follows the
  // hand instead of snapping its centre under it.
  if (origin.mode === "grab") {
    const moved = at - origin.atDown
    return { start: clampWindowValue(origin.startAtDown + moved, 0, maxStart), size }
  }

  // Jump: centre on the pointer, so pressing the bare track brings that stretch
  // of line into the ribbon.
  return { start: clampWindowValue(at - Math.round(size / 2), 0, maxStart), size }
}

// Whether a span index falls inside the window — a grab rather than a jump.
export const isInsideWindow = (at: number, start: number, size: number): boolean =>
  at >= start && at < start + size
